import React from 'react';
import productLockIcon from 'images/utils/product_lock.png';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import formatUSD from '../../../utils/formatUSD.js';
import { colorModeStyle } from '../../../utils/contrastColor.js';
import { catalogLayoutSelector, anyColorSetSelector } from '../../../selectors';
// Shared with the suggested / additional-offer tiles, so a you-pick or
// build-your-own parent always opens its slot flow (ENG-1683).
import { getProductLink } from '../shared/ProductLink';

const NthFreePrice = styled.p`
  margin-top: 2px;
  font-size: 14px;
`;

const CategoryProduct = ({
  isNthFree,
  product,
  unlockedGroups,
  checkerWhite = false,
}) => {
  const shoot = useSelector((state) => state.shoot?.shoot);
  const isStrawbridge =
    shoot?.company_auth_token?.toLowerCase() === 'strawbridge';
  const catalogLayout = useSelector(catalogLayoutSelector);
  const anyColorSet = useSelector(anyColorSetSelector);
  const isHero = (product) => product.display_type === 'display_full';

  // Single-column layout is authoritative and OVERRIDES per-product display_type:
  // in single mode nothing renders as the full-width "most popular" hero — every
  // card is a uniform full-width stretched card. The hero (display_full) only
  // applies in double-column layout.
  const usesHeroLayout = (product) =>
    isHero(product) && catalogLayout !== 'single';

  const className = (product) => {
    // Alternating checkerboard-white background, applied by the parent's
    // positional flag. Only the .bnl-product-regular cards get it (never the
    // hero); in color mode the parent leaves it false so the inline color wins.
    const checker = checkerWhite ? ' bnl-product-checker-white' : '';

    // Single-column overrides display_type: every card is a uniform full-width
    // stretched card (no "most popular" hero).
    if (catalogLayout === 'single') {
      return `bnl-product-regular bnl-product-regular--full col-xs-12 col-md-12 col-lg-12 cursor-pointer${checker}`;
    }

    // Double-column: display_type=display_full designates the full-width hero.
    if (isHero(product)) {
      // Hero keeps the elaborate "most popular" banner DOM at full width.
      return 'bnl-product-most-popular col-xs-12 col-md-12 col-lg-12';
    }

    // Double-column default (today's behavior): half-width cards.
    return `bnl-product-regular col-xs-12 col-md-6 col-lg-6 cursor-pointer${checker}`;
  };

  // Check if product is a voucher
  const isVoucher = (product) => {
    return product.is_voucher === true || product.product_type === 'voucher';
  };

  // Color mode is active only when at least one product in the pricelist set a
  // color; otherwise (existing pricelists) no inline style is applied and the
  // CSS classes drive color exactly as today.
  const colorStyle = anyColorSet ? colorModeStyle(product.color) : undefined;

  // featured_text overrides the hero's legacy label; on a hero with no
  // featured_text the legacy label is preserved. Suppressed for Strawbridge /
  // vouchers (unchanged behavior).
  const heroLabel = (product) => {
    if (isStrawbridge || isVoucher(product)) return null;
    return product.featured_text
      ? ` - ${product.featured_text}`
      : ' - Our Most Popular Option';
  };
  const cardLabel = (product) => {
    if (isVoucher(product) || !product.featured_text) return null;
    return ` - ${product.featured_text}`;
  };

  // Format voucher price display
  const renderVoucherPrice = (product) => {
    const savings = product.total_value_cents - product.purchase_price_cents;
    const hasSavings = savings > 0;

    return (
      <>
        <div>{formatUSD(product.price)}</div>
        <div
          style={{
            fontSize: '12px',
            color: '#166534',
            backgroundColor: '#ecfdf5',
            padding: '4px 8px',
            borderRadius: '4px',
            marginTop: '4px',
          }}
        >
          Get {formatUSD(product.total_value_cents)} value
          {hasSavings && (
            <>
              {' '}
              -{' '}
              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                Save {formatUSD(savings)}
              </span>
            </>
          )}
        </div>
      </>
    );
  };

  const renderProductImage = (product) => {
    if (product.display_image_in_list) {
      return (
        <div className="bnl-product-regular__image-container">
          <img
            className="bnl-product-regular__image"
            src={product.image_thumbnail}
            alt={product.name}
            loading="lazy"
          />
        </div>
      );
    }

    return null;
  };

  const handleProductClick = (product, event) => {
    let locked = false;

    if (product.lock_type === 'unlockable') {
      locked = true;

      product.lock_group.forEach((set) => {
        if (unlockedGroups.includes(set)) locked = false;
      });
    }

    if (locked) {
      event.preventDefault();
    }
  };

  const renderContent = (product, unlockedGroups) => {
    // TODO: add lock/unlock logic
    const productIsVoucher = isVoucher(product);

    if (usesHeroLayout(product)) {
      const imageContainer = 'bnl-product-most-popular__image-container';

      return (
        <>
          <Link
            to={getProductLink(product)}
            onClick={(event) => handleProductClick(product, event)}
          />
          <div className="row">
            <div className="bnl-product-most-popular-left col-xs-12 col-md-5 col-lg-5">
              <h3 className="bnl-product-most-popular__header">
                {product.name}
                {heroLabel(product)}
              </h3>
              {isStrawbridge &&
                !!(product.contents && product.contents.length > 0) && (
                  <p>
                    <b> {product.description} </b>
                  </p>
                )}
              <div className={`${imageContainer} ${imageContainer}--mobile`}>
                <img
                  className="bnl-product-most-popular__image"
                  src={product.image_large || product.image_thumbnail}
                />
              </div>
              <div className="bnl-product__items row">
                {renderContentsOrDesc(product)}
              </div>
              <div className="bnl-product__button button--round-border bg--white-black cursor-pointer">
                {productIsVoucher ? (
                  <>
                    Get {product.name} - Pay {formatUSD(product.price)}, Get{' '}
                    {formatUSD(product.total_value_cents)}
                  </>
                ) : (
                  <>
                    Get {product.name} - {formatUSD(product.price)}
                  </>
                )}
              </div>
            </div>
            <div className="bnl-product-most-popular-right col-xs-12 col-md-7 col-lg-7">
              <div className="bnl-product-most-popular__image-container">
                <img
                  className="bnl-product-most-popular__image"
                  src={product.image_large || product.image_thumbnail}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    if (product.display_product_in_list) {
      return (
        <>
          <Link
            to={getProductLink(product)}
            onClick={(event) => handleProductClick(product, event)}
          />
          <div className="row">
            <h3 className="bnl-product__name col-xs-8 col-md-8 col-lg-8">
              {product.name}
              {cardLabel(product)}
            </h3>
            <div className="bnl-product__price col-xs-4 col-md-4 col-lg-4">
              {productIsVoucher ? (
                renderVoucherPrice(product)
              ) : (
                <>
                  {product.price === 0 ? 'Free' : formatUSD(product.price)}
                  {isNthFree && (
                    <NthFreePrice>
                      value: {formatUSD(product.nth_free_value)}
                    </NthFreePrice>
                  )}
                </>
              )}
            </div>
          </div>
          {renderProductImage(product)}
          <div className="bnl-product-regular__desc">{product.description}</div>
          <div className="bnl-product__items row">
            {renderProductContents(product)}
          </div>
        </>
      );
    }

    return null;
  };

  const renderProductContents = (product) => {
    // Skip contents for voucher products (they show credit/expiration info that's not applicable here)
    if (isVoucher(product)) return null;

    if (product.contents && product.contents.length > 0) {
      const contents = [];
      let lists = [];
      let midPoint;

      if (product.contents.length % 2 === 0) {
        midPoint = product.contents.length / 2 - 1;
      } else {
        midPoint = Math.floor(product.contents.length / 2);
      }

      for (let i = 0; i <= midPoint; i++) {
        const content = product.contents[i];
        lists.push(<li key={`${product.id}-${content}-${i}`}>{content}</li>);
      }

      contents.push(
        <ul className="col-xs-12 col-md-6 col-lg-6" key={`${product.id}-left`}>
          {lists}
        </ul>,
      );
      lists = [];

      for (let i = midPoint + 1; i < product.contents.length; i++) {
        const content = product.contents[i];
        lists.push(<li key={`${product.id}-${content}-${i}`}>{content}</li>);
      }

      contents.push(
        <ul className="col-xs-12 col-md-6 col-lg-6" key={`${product.id}-right`}>
          {lists}
        </ul>,
      );

      return contents;
    }
  };

  const renderContentsOrDesc = (product) => {
    // Skip contents for voucher products (they show credit/expiration info that's not applicable here)
    if (isVoucher(product)) return null;

    if (product.contents && product.contents.length > 0) {
      const contents = [];
      let lists = [];
      let midPoint;

      if (product.contents.length % 2 === 0) {
        midPoint = product.contents.length / 2 - 1;
      } else {
        midPoint = Math.floor(product.contents.length / 2);
      }

      for (let i = 0; i <= midPoint; i++) {
        const content = product.contents[i];
        lists.push(<li key={`${product.id}-${content}-${i}`}>{content}</li>);
      }

      contents.push(
        <ul className="col-xs-12 col-md-6 col-lg-6" key={`${product.id}-left`}>
          {lists}
        </ul>,
      );
      lists = [];

      for (let i = midPoint + 1; i < product.contents.length; i++) {
        const content = product.contents[i];
        lists.push(<li key={`${product.id}-${content}-${i}`}>{content}</li>);
      }

      contents.push(
        <ul className="col-xs-12 col-md-6 col-lg-6" key={`${product.id}-right`}>
          {lists}
        </ul>,
      );

      return contents;
    }
    if (product.description) {
      return (
        <div className="bnl-product__description">{product.description}</div>
      );
    }
  };

  const renderLock = (product, unlockedGroups) => {
    if (product.lock_type === 'unlockable') {
      let locked = true;

      product.lock_group.forEach((set) => {
        if (unlockedGroups.includes(set)) locked = false;
      });

      if (locked) {
        return (
          <img
            className="bnl-product__lock-image"
            alt="lock icon"
            src={productLockIcon}
          />
        );
      }
    }

    return null;
  };

  return (
    <li
      className={`${className(product)}${
        isStrawbridge && usesHeroLayout(product) ? ' strawbridge-product' : ''
      }`}
      style={colorStyle}
      key={product.id}
    >
      {renderLock(product, unlockedGroups)}
      {renderContent(product, unlockedGroups)}
    </li>
  );
};

CategoryProduct.propTypes = {
  unlockedGroups: PropTypes.array.isRequired,
};

export default CategoryProduct;
